import { Injectable, OnDestroy } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SignalRService implements OnDestroy {
  private _hubConnection: signalR.HubConnection | null = null;
  private _connectionState$ = new BehaviorSubject<signalR.HubConnectionState>(signalR.HubConnectionState.Disconnected);
  private _destroy$ = new Subject<void>();

  get connectionState$(): Observable<signalR.HubConnectionState> {
    return this._connectionState$.asObservable();
  }

  get isConnected(): boolean {
    return this._hubConnection?.state === signalR.HubConnectionState.Connected;
  }

  constructor(private _msalService: MsalService) {}

  async startConnection(): Promise<void> {
    if (this._hubConnection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    this._hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.api}/hubs/precalc`, {
        accessTokenFactory: async () => {
          const result = await firstValueFrom(
            this._msalService.acquireTokenSilent({ scopes: environment.apiConfig.scopes })
          );
          if (!result.accessToken) {
            throw new Error('No access token available');
          }
          return result.accessToken;
        },
      })
      .configureLogging(signalR.LogLevel.Warning)
      .withAutomaticReconnect()
      .build();

    this._hubConnection.onreconnecting(() => this._connectionState$.next(signalR.HubConnectionState.Reconnecting));
    this._hubConnection.onreconnected(() => this._connectionState$.next(signalR.HubConnectionState.Connected));
    this._hubConnection.onclose(() => this._connectionState$.next(signalR.HubConnectionState.Disconnected));

    await this._hubConnection.start();
    this._connectionState$.next(signalR.HubConnectionState.Connected);
  }

  async stopConnection(): Promise<void> {
    if (!this._hubConnection) return;
    await this._hubConnection.stop();
    this._hubConnection = null;
  }

  on<T>(eventName: string): Observable<T> {
    return new Observable<T>((observer) => {
      const handler = (data: T) => observer.next(data);
      this._hubConnection?.on(eventName, handler);
      return () => this._hubConnection?.off(eventName, handler);
    });
  }

  async invoke<T>(methodName: string, ...args: unknown[]): Promise<T> {
    if (!this.isConnected) {
      throw new Error('SignalR connection is not established  using ');
    }
    return this._hubConnection!.invoke<T>(methodName, ...args);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this.stopConnection();
  }
}
